import type Konva from "konva";
import { useEffect, useRef } from "react";

type HjWindow = Window & { hj?: (...args: unknown[]) => void };

const CAPTURE_INTERVAL_MS = 1000;
const CAPTURE_PIXEL_RATIO = 1;
const CAPTURE_QUALITY = 0.6;

/**
 * Periodically snapshots the Konva stage canvas to an `<img>` element so that
 * Hotjar (which cannot record `<canvas>` content) can capture it in session
 * replays. The `<img>` should be rendered behind the canvas — in normal
 * browsing the live canvas occludes it, but in Hotjar replays (where canvas is
 * transparent) the snapshot shows through.
 *
 * No-ops when Hotjar is not loaded on the page.
 */
export function useHotjarCanvasCapture(
  stageRef: React.RefObject<Konva.Stage | null>
): React.RefObject<HTMLImageElement | null> {
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    let tainted = false;

    const intervalId = setInterval(() => {
      if (tainted) return;
      if (typeof (window as HjWindow).hj !== "function") return;
      if (document.hidden) return;

      const stage = stageRef.current;
      const img = imgRef.current;
      if (!stage || !img || stage.width() === 0 || stage.height() === 0) return;

      try {
        img.src = stage.toDataURL({
          pixelRatio: CAPTURE_PIXEL_RATIO,
          mimeType: "image/jpeg",
          quality: CAPTURE_QUALITY
        });
      } catch {
        tainted = true;
        clearInterval(intervalId);
      }
    }, CAPTURE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [stageRef]);

  return imgRef;
}
